import type { ComponentType } from "react";

import type { ChainWriteOutcome } from "src/components/useGuardrailChainActions";

import {
  isInboxItemKind,
  type InboxItemKind,
  type ParsedInboxKey,
} from "./kinds";
import type { InboxRow } from "./row";
import ForecastInboxCard from "./forecast/Card";
import ForecastInboxDetail from "./forecast/Detail";
import ForecastMissing from "./forecast/Missing";
import {
  parseForecastInboxRow,
  type ForecastSubmissionItem,
} from "./forecast/item";
import RequisitionInboxCard from "./requisition/Card";
import RequisitionInboxDetail from "./requisition/Detail";
import RequisitionMissing from "./requisition/Missing";
import {
  afterRequisitionRemoval,
  parseRequisitionInboxRow,
  type RequisitionInboxItem,
} from "./requisition/item";

/**
 * How to add a kind:
 * 1. Add the GraphQL type + fragment; spread it on `inbox.graphql` items.
 * 2. Add the kind to `InboxItemKind` and `categoryOf` in `kinds.ts`.
 * 3. Put Card, Detail, parse, Missing (and afterRemoval if you jump) next to that domain.
 * 4. Extend the `InboxItem` union and add a `case` in `inboxKind()` (exhaustive `never`).
 */
export type InboxItem = RequisitionInboxItem | ForecastSubmissionItem;

export type InboxDetailProps = {
  item: InboxItem;
  readOnly: boolean;
  onDecided?: (decision: {
    response: ChainWriteOutcome;
    requisitionId: string;
    displayId: string;
    title: string;
  }) => void;
  onRespondStart?: () => void;
  onRespondError?: () => void;
  autoFocusTitle?: boolean;
};

export type InboxKindPack = {
  Card: ComponentType<{
    item: InboxItem;
    selected: boolean;
    arrived?: boolean;
    onSelect: (key: string) => void;
  }>;
  Detail: ComponentType<InboxDetailProps>;
  Missing: ComponentType<{ parsed: ParsedInboxKey }>;
  parse: (row: InboxRow) => InboxItem | null;
  afterRemoval?: (
    removed: InboxItem,
    remaining: readonly InboxItem[]
  ) => string | null;
};

const requisitionPack = {
  Card: RequisitionInboxCard,
  Detail: RequisitionInboxDetail,
  Missing: RequisitionMissing,
  parse: parseRequisitionInboxRow,
  afterRemoval: afterRequisitionRemoval,
} as InboxKindPack;

const forecastPack = {
  Card: ForecastInboxCard,
  Detail: ForecastInboxDetail,
  Missing: ForecastMissing,
  parse: parseForecastInboxRow,
} as InboxKindPack;

export const inboxKind = (kind: InboxItemKind): InboxKindPack => {
  switch (kind) {
    case "REQUISITION_APPROVAL":
    case "REQUISITION_EXCEPTION_REASON":
      return requisitionPack;
    case "FORECAST_SUBMISSION":
      return forecastPack;
    default: {
      const unexpected: never = kind;
      return unexpected;
    }
  }
};

export const parseInboxRow = (row: InboxRow): InboxItem | null => {
  if (!isInboxItemKind(row.kind)) {
    return null;
  }
  return inboxKind(row.kind).parse(row);
};
